/* eslint-disable */
import {
	useEffect,
	useRef,
	useState,
	type MouseEvent as ReactMouseEvent,
} from "react";

import JoinModal from "@/components/landing/JoinModal";
import LoginModal from "@/components/landing/LoginModal";
import Link from "next/link";
import { useRouter } from "next/router";
import { useDispatch } from "react-redux";
import { actionJoinModalHandler } from "@/redux";
import ResetPasswordModal from "@/components/landing/ResetPasswordModal";
import ResendEmailVerificationModal from "@/components/landing/ResendEmailVerificationModal";

import { useTranslation } from "next-i18next";
import Cookies from "js-cookie";

import { HiOutlineMenuAlt4, HiOutlineX } from "react-icons/hi";
import LanguageFrame from "./LanguagesFrame";
import CountryFlag from "../shared/CountryFlag";

const APP_LOGIN_URL = "https://app.kalonwallet.com/auth/login";

function localeFlagCode(locale: string) {
	if (locale === "tr") return "TR";
	if (locale === "ar") return "SA";
	return "US";
}

export default function Header({ theme }: any) {
	const router = useRouter();
	const [isLogin, setIsLogin] = useState(false);
	const [mobileMenu, setMobileMenu] = useState(false);
	const dispatch = useDispatch();
	const { t } = useTranslation();
	const [languageModal, setLanguageModal] = useState<boolean>(false);
	const desktopLangRef = useRef<HTMLButtonElement>(null);
	const mobileLangRef = useRef<HTMLButtonElement>(null);
	const [langAnchor, setLangAnchor] = useState<"desktop" | "mobile">(
		"desktop",
	);
	const activeLocale = router.locale || "en";
	const activeFlag = localeFlagCode(activeLocale);
	const [isScrolled, setIsScrolled] = useState(false);

	useEffect(() => {
		if (!router.isReady) return;
		if (router.query?.ref) {
			dispatch(actionJoinModalHandler());
		}
	}, [router.isReady, router.query, dispatch]);

	function handleLangClose() {
		setLanguageModal(false);
	}

	useEffect(() => {
		if (!router.isReady) return;
		try {
			localStorage.setItem("lang", activeLocale);
		} catch {
			// Ignore storage failures (private mode, etc.)
		}
	}, [router.isReady, activeLocale]);

	useEffect(() => {
		try {
			var access_data = Cookies.get("access");
			if (
				access_data != undefined &&
				access_data != null &&
				access_data != ""
			) {
				setIsLogin(true);
			} else {
				setIsLogin(false);
			}
		} catch {
			setIsLogin(false);
		}
	}, []);

	useEffect(() => {
		const handleScroll = () => {
			setIsScrolled(window.scrollY > 8);
		};
		window.addEventListener("scroll", handleScroll);
		return () => window.removeEventListener("scroll", handleScroll);
	}, []);

	useEffect(() => {
		if (mobileMenu) {
			document.body.style.overflow = "hidden";
		} else {
			document.body.style.overflow = "auto";
		}
		return () => {
			document.body.style.overflow = "auto";
		};
	}, [mobileMenu]);

	useEffect(() => {
		const closeOnRoute = () => setMobileMenu(false);
		router.events.on("routeChangeComplete", closeOnRoute);
		return () => router.events.off("routeChangeComplete", closeOnRoute);
	}, [router.events]);

	const navLinkClass = (path: string, exact = false) => {
		const active = exact
			? router.pathname === path
			: router.pathname === path || router.pathname.startsWith(path + "/");
		return `landing-nav-link text-sm font-medium tracking-wide transition-colors ${
			active ? "text-kalon-text" : "text-kalon-muted hover:text-kalon-text"
		}`;
	};

	const openApp = () => {
		window.open(APP_LOGIN_URL, "_SELF");
	};

	/** Close drawer and navigate with the same router instance Header already uses. */
	const goMobile =
		(href: string) => (event: ReactMouseEvent<HTMLAnchorElement>) => {
			event.preventDefault();
			setMobileMenu(false);
			void router.push(href).catch(() => {
				// Soft nav can be cancelled (e.g. stale-build guard). Still honor the tap.
				window.location.assign(href);
			});
		};

	return (
		<>
			<header
				className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${
					isScrolled || mobileMenu
						? "border-b border-[rgba(147,197,253,0.14)] bg-[#080D1B]/92 backdrop-blur-xl shadow-[0_8px_30px_rgba(4,8,24,0.35)]"
						: "border-b border-transparent bg-[#080D1B]/55 backdrop-blur-md"
				}`}>
				<div className="landing-container flex h-16 items-center justify-between gap-4 px-4 lg:h-[72px] lg:px-6">
					<Link
						href="/"
						className="landing-brand relative z-10 flex shrink-0 items-center"
						aria-label={t("kalonWallet")}>
						{t("kalonWallet")}
					</Link>

					<nav
						className="hidden items-center gap-8 lg:flex"
						aria-label="Primary">
						<Link href="/" className={navLinkClass("/", true)}>
							{t("landing.home")}
						</Link>
						<Link href="/education" className={navLinkClass("/education")}>
							{t("edu.education")}
						</Link>
						<div className="group relative">
							<Link
								href="/analyses"
								className={navLinkClass("/analyses")}>
								{t("landing.services")}
							</Link>
							<ul className="invisible absolute left-1/2 top-full z-50 mt-3 w-56 -translate-x-1/2 rounded-2xl border border-[rgba(147,197,253,0.14)] bg-[#101A30]/98 p-2 opacity-0 shadow-[0_18px_50px_rgba(4,8,24,0.55)] backdrop-blur-xl transition-all duration-200 group-hover:visible group-hover:opacity-100 group-focus-within:visible group-focus-within:opacity-100">
								<li>
									<Link
										href="/analyses"
										className="block rounded-xl px-3 py-2.5 text-sm text-kalon-muted transition-colors hover:bg-[#202F4B] hover:text-kalon-text">
										{t("analyze.analyses")}
									</Link>
								</li>
								<li>
									<Link
										href="/money-management"
										className="block rounded-xl px-3 py-2.5 text-sm text-kalon-muted transition-colors hover:bg-[#202F4B] hover:text-kalon-text">
										{t("landing.assetManagement")}
									</Link>
								</li>
							</ul>
						</div>
						<div className="group relative">
							<Link href="/about" className={navLinkClass("/about")}>
								{t("landing.company")}
							</Link>
							<ul className="invisible absolute left-1/2 top-full z-50 mt-3 w-56 -translate-x-1/2 rounded-2xl border border-[rgba(147,197,253,0.14)] bg-[#101A30]/98 p-2 opacity-0 shadow-[0_18px_50px_rgba(4,8,24,0.55)] backdrop-blur-xl transition-all duration-200 group-hover:visible group-hover:opacity-100 group-focus-within:visible group-focus-within:opacity-100">
								<li>
									<Link
										href="/about"
										className="block rounded-xl px-3 py-2.5 text-sm text-kalon-muted transition-colors hover:bg-[#202F4B] hover:text-kalon-text">
										{t("about.aboutUs")}
									</Link>
								</li>
								<li>
									<Link
										href="/contact-us"
										className="block rounded-xl px-3 py-2.5 text-sm text-kalon-muted transition-colors hover:bg-[#202F4B] hover:text-kalon-text">
										{t("contact.contactUs")}
									</Link>
								</li>
							</ul>
						</div>
						<Link href="/faqs" className={navLinkClass("/faqs")}>
							{t("faq.faq")}
						</Link>
					</nav>

					<div className="hidden items-center gap-2 lg:flex">
						<button
							ref={desktopLangRef}
							type="button"
							aria-haspopup="menu"
							aria-expanded={languageModal && langAnchor === "desktop"}
							onClick={() => {
								setLangAnchor("desktop");
								setLanguageModal((open) =>
									langAnchor === "desktop" ? !open : true,
								);
							}}
							className="landing-nav-link inline-flex items-center gap-1.5 rounded-xl px-3 py-2 text-sm font-semibold text-kalon-muted transition-colors hover:bg-white/5 hover:text-kalon-text"
							aria-label={activeLocale.toUpperCase()}>
							<span className="flex h-4 w-4 shrink-0 items-center overflow-hidden rounded-full">
								<CountryFlag
									code={activeFlag}
									size="S"
									hasBorderRadius={true}
									className="border-none"
								/>
							</span>
							{activeLocale.toUpperCase()}
						</button>
						<button
							type="button"
							onClick={openApp}
							className="landing-btn-secondary !min-h-10 !px-4 !text-sm">
							{t("landing.login")}
						</button>
						<button
							type="button"
							onClick={openApp}
							className="landing-btn-primary !min-h-10 !px-4 !text-sm">
							{t("landing.signup")}
						</button>
					</div>

					<div className="flex items-center gap-2 lg:hidden">
						<button
							type="button"
							onClick={openApp}
							className="landing-btn-primary !min-h-10 !px-3 !text-xs">
							{t("landing.login")}
						</button>
						<button
							type="button"
							aria-expanded={mobileMenu}
							aria-controls="mobile-nav"
							aria-label={mobileMenu ? "Close menu" : "Open menu"}
							onClick={() => setMobileMenu((v) => !v)}
							className="inline-flex h-10 w-10 items-center justify-center rounded-xl border border-[rgba(147,197,253,0.14)] bg-[#18243C] text-kalon-text transition-colors hover:border-kalon-accent/50">
							{mobileMenu ? (
								<HiOutlineX className="text-xl" />
							) : (
								<HiOutlineMenuAlt4 className="text-xl" />
							)}
						</button>
					</div>
				</div>
			</header>

			{/* Mobile drawer — z above header so taps aren't stolen by the fixed bar */}
			<div
				id="mobile-nav"
				className={`fixed inset-0 z-[60] lg:hidden ${
					mobileMenu ? "pointer-events-auto" : "pointer-events-none"
				}`}
				aria-hidden={!mobileMenu}>
				<button
					type="button"
					tabIndex={mobileMenu ? 0 : -1}
					className={`absolute inset-0 z-0 bg-black/60 transition-opacity ${
						mobileMenu ? "opacity-100" : "opacity-0"
					}`}
					aria-label="Close menu"
					onClick={() => setMobileMenu(false)}
				/>
				<aside
					className="absolute right-0 top-0 z-10 flex h-full w-[min(100%,320px)] flex-col border-l border-[rgba(147,197,253,0.14)] bg-[#101A30] shadow-2xl transition-transform duration-300 ease-out will-change-transform"
					style={{
						// Use transform (not Tailwind v4 `translate-*`) so open/close
						// cannot stick at translate: 100% during transitions.
						// Explicitly clear the individual `translate` property so it
						// cannot compose with transform and keep the drawer off-screen.
						translate: "none",
						transform: mobileMenu
							? "translate3d(0,0,0)"
							: "translate3d(100%,0,0)",
					}}>
					<div className="flex items-center justify-between border-b border-[rgba(147,197,253,0.14)] px-5 py-4">
						<span className="landing-brand-sm">{t("kalonWallet")}</span>
						<button
							type="button"
							onClick={() => setMobileMenu(false)}
							className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-[rgba(147,197,253,0.14)] text-kalon-muted"
							aria-label="Close menu">
							<HiOutlineX className="text-lg" />
						</button>
					</div>
					<nav className="flex flex-1 flex-col gap-1 overflow-y-auto p-4">
						<Link
							href="/"
							onClick={goMobile("/")}
							className="rounded-xl px-3 py-3 text-sm font-medium text-kalon-text hover:bg-[#202F4B]">
							{t("landing.home")}
						</Link>
						<Link
							href="/education"
							onClick={goMobile("/education")}
							className="rounded-xl px-3 py-3 text-sm font-medium text-kalon-muted hover:bg-[#202F4B] hover:text-kalon-text">
							{t("edu.education")}
						</Link>
						<Link
							href="/analyses"
							onClick={goMobile("/analyses")}
							className="rounded-xl px-3 py-3 text-sm font-medium text-kalon-muted hover:bg-[#202F4B] hover:text-kalon-text">
							{t("analyze.analyses")}
						</Link>
						<Link
							href="/money-management"
							onClick={goMobile("/money-management")}
							className="rounded-xl px-3 py-3 text-sm font-medium text-kalon-muted hover:bg-[#202F4B] hover:text-kalon-text">
							{t("landing.assetManagement")}
						</Link>
						<Link
							href="/about"
							onClick={goMobile("/about")}
							className="rounded-xl px-3 py-3 text-sm font-medium text-kalon-muted hover:bg-[#202F4B] hover:text-kalon-text">
							{t("about.aboutUs")}
						</Link>
						<Link
							href="/contact-us"
							onClick={goMobile("/contact-us")}
							className="rounded-xl px-3 py-3 text-sm font-medium text-kalon-muted hover:bg-[#202F4B] hover:text-kalon-text">
							{t("contact.contactUs")}
						</Link>
						<Link
							href="/faqs"
							onClick={goMobile("/faqs")}
							className="rounded-xl px-3 py-3 text-sm font-medium text-kalon-muted hover:bg-[#202F4B] hover:text-kalon-text">
							{t("faq.faq")}
						</Link>
					</nav>
					<div className="space-y-3 border-t border-[rgba(147,197,253,0.14)] p-4">
						<button
							ref={mobileLangRef}
							type="button"
							aria-haspopup="menu"
							aria-expanded={languageModal && langAnchor === "mobile"}
							onClick={() => {
								setLangAnchor("mobile");
								setLanguageModal((open) =>
									langAnchor === "mobile" ? !open : true,
								);
							}}
							className="flex w-full items-center justify-center gap-1.5 rounded-xl border border-[rgba(147,197,253,0.14)] px-4 py-2.5 text-sm font-semibold text-kalon-muted">
							<span className="flex h-4 w-4 shrink-0 items-center overflow-hidden rounded-full">
								<CountryFlag
									code={activeFlag}
									size="S"
									hasBorderRadius={true}
									className="border-none"
								/>
							</span>
							{activeLocale.toUpperCase()}
						</button>
						<button
							type="button"
							onClick={openApp}
							className="landing-btn-primary w-full">
							{t("landing.signup")} / {t("landing.login")}
						</button>
					</div>
				</aside>
			</div>

			<LoginModal />
			<JoinModal />
			<ResetPasswordModal />
			<ResendEmailVerificationModal />
			<LanguageFrame
				show={languageModal}
				onHide={handleLangClose}
				anchorRef={
					langAnchor === "desktop" ? desktopLangRef : mobileLangRef
				}
			/>
		</>
	);
}
